import React from 'react';
import { Handle, Position } from 'reactflow';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Layers, ChevronDown } from 'lucide-react';
import { VACANT_WARN_DAYS } from '../orgChartModel';

// Aynı üst pozisyon altında aynı ünvanlı ≥3 boş koltuğun tek kartı: "Müşteri Temsilcisi —
// 18 Boş Koltuk". Kartın tamamı tıklanabilir: grup açılır ve koltuklar tek tek çizilir.
// Kart, OrgCard ile aynı kutu boyutunu kullanır (yerleşim tek boyutla hesaplanıyor);
// arkadaki "yığın" gölgesi birden çok koltuğu temsil ettiğini görsel olarak söyler.

const Wrapper = styled.button`
  width: 232px;
  height: 72px;
  padding: 0 12px 0 14px;
  background: #FAFBFC;
  border: 1px dashed #C3CBD5;
  border-radius: 14px;
  box-shadow: 4px 4px 0 -1px #F5F7FA, 4px 4px 0 0 #E6EAF0;
  font-family: 'Nunito Sans', sans-serif;
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  position: relative;
  /* OrgCard ile aynı gerekçe: React Flow salt okunur şemada sarmalayıcıya
     pointer-events: none yazar, kart kendi vuruş testini geri açar. */
  pointer-events: auto;
  &:hover { border-color: rgba(2, 145, 239, 0.45); }
`;

const Icon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #EEF1F5;
  color: ${({ $warn }) => ($warn ? '#E53935' : '#A0A8B4')};
  border: 1.5px dashed ${({ $warn }) => ($warn ? '#E53935' : '#C3CBD5')};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1F2A37;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Sub = styled.div`
  font-size: 11px;
  color: #6B7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const hiddenHandle = { opacity: 0, width: 1, height: 1, minWidth: 0, minHeight: 0, border: 0, pointerEvents: 'none' };

export default function VacantGroupNode({ data }) {
  const { t } = useTranslation();
  const warn = typeof data.longestVacantDays === 'number' && data.longestVacantDays > VACANT_WARN_DAYS;
  const title = t('orgChart.scope.groupTitle', { title: data.titleName || '-', count: data.count });
  const details = [
    data.openRequisitionCount > 0 ? t('orgChart.scope.groupOpenRequisitions', { count: data.openRequisitionCount }) : null,
    typeof data.longestVacantDays === 'number' ? t('orgChart.scope.groupLongestVacant', { count: data.longestVacantDays }) : null,
  ].filter(Boolean).join(' · ');

  return (
    <Wrapper
      type="button"
      data-testid="vacant-group"
      title={title}
      $warn={warn}
      onClick={(e) => {
        e.stopPropagation();
        data.onToggleGroup?.(data.groupKey);
      }}
    >
      <Handle type="target" position={Position.Top} style={hiddenHandle} isConnectable={false} />
      <Icon $warn={warn}><Layers size={16} /></Icon>
      <Body>
        <Title>{t('orgChart.vacantGroupCard', { count: data.count })}</Title>
        <Sub>{[data.titleName, details].filter(Boolean).join(' · ') || t('orgChart.scope.groupExpand')}</Sub>
      </Body>
      <ChevronDown size={14} color="#0291EF" />
      <Handle type="source" position={Position.Bottom} style={hiddenHandle} isConnectable={false} />
    </Wrapper>
  );
}
